import { useCallback, useState } from 'react';
import type { Command } from '../../menu/types';
import { getCommandHandler, defaultCommandHandler } from '../commands';
import { getCommandId } from '../commands/utils';
import type { CommandContext, CommandResult } from '../commands';

type UseCommandHandlerProps = Omit<CommandContext, 'metadata' | 'columns' | 'commands'> & {
  metadata?: CommandContext['metadata'];
  columns?: CommandContext['columns'];
  commands?: CommandContext['commands'];
};

type UseCommandHandlerReturn = {
  executeCommand: (command: Command) => Promise<CommandResult>;
  isExecuting: boolean;
  lastResult: CommandResult | null;
};

/**
 * Hook for executing grid commands
 * Looks up command handlers from registry and provides execution state
 *
 * @example
 * const { executeCommand, isExecuting } = useCommandHandler({
 *   metadata,
 *   columns: parsedColumns,
 *   gridData,
 *   displayData,
 *   hasUnsavedChanges,
 *   modifiedRows,
 *   modifiedCells,
 *   markedForDeletion,
 *   onRefresh: refetchGrid,
 *   onSave: handleBatchSave,
 *   onCancel: handleCancelAll,
 *   navigate: navGuard.requestNavigation,
 *   currentPath: pathname,
 * });
 */
export function useCommandHandler(props: UseCommandHandlerProps): UseCommandHandlerReturn {
  const [isExecuting, setIsExecuting] = useState(false);
  const [lastResult, setLastResult] = useState<CommandResult | null>(null);

  // Destructure critical props that should trigger callback recreation
  const {
    metadata,
    pageTitle,
    columns,
    commands,
    gridData,
    displayData,
    exportData,
    exportColumns,
    hasActiveFilters,
    serverSummary,
    selectedRows,
    hasUnsavedChanges,
    modifiedRows,
    modifiedCells,
    markedForDeletion,
    onRefresh,
    onSave,
    onCancel,
    onAddRow,
    onAddRowWithData,
    onDeleteRow,
    onEditRow,
    onResetFilters,
    toggleGridVisibilityFilter,
    navigate,
    currentPath,
    lookupMaps,
    configurationTables,
    setLoading,
    parentId,
    entityId,
    copyRows,
    clearSelection,
    deleteBatchRows,
    updateBatchRows,
    requestConfirmation,
    requestDeleteConfirmation,
    inlineEditable,
    openCompanyBankAccountPicker,
    openEmployeeManagerPicker,
    openSubscriptionProofBankImportDialog,
    openCreateTaskForTeamDialog,
    openCreateTasksAndWorkLogsDialog,
    openMissingEmployeeRatesDialog,
    openBankStatementManagementImportDialog,
    dateRange,
    onReplaceGridView,
    generateAndSendPayroll,
    groupFields,
    groupCaptions,
  } = props;

  const executeCommand = useCallback(
    async (command: Command): Promise<CommandResult> => {
      // Validate that we have required context
      if (!metadata || !columns) {
        const result: CommandResult = {
          success: false,
          error: 'Command context is incomplete (missing metadata or columns)',
        };
        setLastResult(result);
        return result;
      }

      // Build complete context
      const context: CommandContext = {
        metadata,
        pageTitle,
        columns,
        commands,
        gridData,
        displayData,
        exportData,
        exportColumns,
        hasActiveFilters,
        serverSummary,
        selectedRows,
        hasUnsavedChanges,
        modifiedRows,
        modifiedCells,
        markedForDeletion,
        onRefresh,
        onSave,
        onCancel,
        onAddRow,
        onAddRowWithData,
        onDeleteRow,
        onEditRow,
        onResetFilters,
        toggleGridVisibilityFilter,
        navigate,
        currentPath,
        lookupMaps,
        configurationTables,
        setLoading,
        parentId,
        entityId,
        copyRows,
        clearSelection,
        deleteBatchRows,
        updateBatchRows,
        requestConfirmation,
        requestDeleteConfirmation,
        inlineEditable,
        openCompanyBankAccountPicker,
        openEmployeeManagerPicker,
        openSubscriptionProofBankImportDialog,
        openCreateTaskForTeamDialog,
        openCreateTasksAndWorkLogsDialog,
        openMissingEmployeeRatesDialog,
        openBankStatementManagementImportDialog,
        dateRange,
        onReplaceGridView,
        generateAndSendPayroll,
        groupFields,
        groupCaptions,
      };

      setIsExecuting(true);

      try {
        // Resolve handler by effective id (custom commands use action), then raw alias
        const commandId = getCommandId(command);
        let handler =
          getCommandHandler(commandId) ||
          getCommandHandler(command.alias) ||
          (command.action ? getCommandHandler(command.action.toLowerCase()) : undefined);

        // Fallback to default handler if no specific handler found
        if (!handler) {
          console.warn(
            `No handler found for command alias: "${command.alias}"${command.action ? ` / action: "${command.action}"` : ''}, using default handler`
          );
          handler = defaultCommandHandler;
        }

        // Execute handler
        const result = await Promise.resolve(handler(command, context));

        setLastResult(result);

        return result;
      } catch (err: unknown) {
        const result: CommandResult = {
          success: false,
          error: err instanceof Error ? err.message : 'Command execution failed',
        };

        setLastResult(result);

        console.error(`Error executing command "${command.alias}":`, err);

        return result;
      } finally {
        setIsExecuting(false);
      }
    },
    [
      metadata,
      pageTitle,
      columns,
      commands,
      gridData,
      displayData,
      exportData,
      exportColumns,
      hasActiveFilters,
      serverSummary,
      selectedRows,
      hasUnsavedChanges,
      modifiedRows,
      modifiedCells,
      markedForDeletion,
      onRefresh,
      onSave,
      onCancel,
      onAddRow,
      onAddRowWithData,
      onDeleteRow,
      onEditRow,
      onResetFilters,
      toggleGridVisibilityFilter,
      navigate,
      currentPath,
      lookupMaps,
      configurationTables,
      setLoading,
      parentId,
      entityId,
      copyRows,
      clearSelection,
      deleteBatchRows,
      updateBatchRows,
      requestConfirmation,
      requestDeleteConfirmation,
      inlineEditable,
      openCompanyBankAccountPicker,
      openEmployeeManagerPicker,
      openSubscriptionProofBankImportDialog,
      openCreateTaskForTeamDialog,
      openCreateTasksAndWorkLogsDialog,
      openMissingEmployeeRatesDialog,
      openBankStatementManagementImportDialog,
      dateRange,
      onReplaceGridView,
      generateAndSendPayroll,
      groupFields,
      groupCaptions,
    ]
  );

  return {
    executeCommand,
    isExecuting,
    lastResult,
  };
}
